// src/pages/InvoiceDetail.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api.js";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/invoice-detail/${id}`, {
        headers: { Authorization: token },
      });
      setInvoice(data.data.invoice);
      setItems(data.data.items || []);
      setSummary(data.data.summary);
    } catch (err) {
      console.error("Fetch invoice detail failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line
  }, [id]);

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading invoice...</div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-6 text-center text-red-500">Invoice not found</div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Invoice Detail
        </h2>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 dark:text-white"
        >
          Back
        </button>
      </div>

      {/* INVOICE INFO */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard title="Invoice Number" value={invoice.id} />
        <InfoCard
          title="Customer"
          value={`${invoice.firstname} ${invoice.lastname}`}
        />
        <InfoCard title="Customer ID" value={invoice.cust_id} />
        <InfoCard title="Invoice Date" value={invoice.date} />
        <InfoCard title="Due Date" value={invoice.duedate} />
        <InfoCard title="Payment Method" value={invoice.paymentmethod || "-"} />
      </div>

      {/* STATUS & TOTAL */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 rounded-xl p-4 shadow bg-white dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
          <StatusBadge status={invoice.status} />
        </div>
        <div className="flex-1 rounded-xl p-4 shadow bg-white dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
          <p className="text-2xl font-bold text-blue-600">
            Rp {Number(summary.total).toLocaleString("id-ID")}
          </p>
        </div>
      </div>

      {/* ITEMS */}
      <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">Description</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-4 py-6 text-center text-gray-500">
                  No items
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t dark:border-gray-700">
                  <td className="px-4 py-3">{item.description}</td>
                  <td className="px-4 py-3">{item.type || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    Rp {Number(item.amount).toLocaleString("id-ID")}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* SUMMARY */}
      <div className="max-w-md ml-auto rounded-xl p-4 shadow bg-white dark:bg-gray-800 space-y-2">
        <SummaryRow label="Subtotal" value={summary.subtotal} />
        <SummaryRow label="Tax" value={summary.tax} />
        <SummaryRow label="Tax 2" value={summary.tax2} />
        <SummaryRow label="Credit" value={summary.credit} />
        <hr className="dark:border-gray-600" />
        <SummaryRow label="Total" value={summary.total} bold />
      </div>
    </div>
  );
}

function InfoCard({ title, value }) {
  return (
    <div className="rounded-xl p-4 shadow bg-white dark:bg-gray-800">
      <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
      <p className="font-medium text-gray-800 dark:text-gray-100">
        {value || "-"}
      </p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Paid: "bg-green-600 text-white",
    Unpaid: "bg-yellow-500 text-black",
    Cancelled: "bg-red-600 text-white",
  };

  return (
    <span
      className={`inline-block mt-1 px-3 py-1 rounded text-sm font-semibold ${
        map[status] || "bg-gray-500 text-white"
      }`}
    >
      {status}
    </span>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <span className={`${bold ? "font-bold" : ""}`}>
        Rp {Number(value).toLocaleString("id-ID")}
      </span>
    </div>
  );
}
